import TrendingStocksCarouselClient from "./TrendingStocksCarouselClient";
import type {
  TrendingStocksAemConfig,
  TrendingStocksInitialSelections,
  TrendingStocksSelectionKey,
} from "./types";
import { TRENDING_STOCKS_PRIMARY_TABS } from "./types";
import { fetchTrendingStocksList } from "./utils/trendingStocksApi";
import { resolveEntityForSelection } from "./utils/trendingStocksHelpers";

const DEFAULT_VIEW_ALL_LINK = "/investments/trending-stocks";

interface TrendingStocksCarouselProps {
  content?: TrendingStocksAemConfig & {
    title?: string;
    heading?: string;
    viewAllText?: string;
    viewAllLink?: string;
    redirectLink?: string;
    openInNewTab?: boolean | string;
    maxItems?: number | string;
  };
}

export default async function TrendingStocksCarousel({
  content,
}: Readonly<TrendingStocksCarouselProps>) {
  const limit = Number(content?.maxItems ?? 10) || 10;

  const allSelections = await Promise.all(
    TRENDING_STOCKS_PRIMARY_TABS.map(async (tab) => {
      const entity = resolveEntityForSelection(
        content?.entityMap,
        tab,
        "all_calls",
        content?.defaultEntity,
      );

      const result = await fetchTrendingStocksList({
        entity,
        tab,
        duration: "all_calls",
        page: 1,
        limit,
        initialExpandedCards: content?.initialExpandedCards,
      });
      const key: TrendingStocksSelectionKey = `${tab}:all_calls`;
      return [key, result] as const;
    }),
  );

  const initialSelections: TrendingStocksInitialSelections = {};
  for (const [key, result] of allSelections) {
    if (result) {
      initialSelections[key] = result;
    }
  }

  const hasItems = Object.values(initialSelections).some(
    (selection) => selection?.items?.length,
  );
  if (!hasItems) return null;

  return (
    <TrendingStocksCarouselClient
      config={content ?? {}}
      initialSelections={initialSelections}
      title={content?.title ?? content?.heading ?? "Trending stocks"}
      viewAllText={content?.viewAllText ?? "View all"}
      viewAllLink={
        content?.viewAllLink ?? content?.redirectLink ?? DEFAULT_VIEW_ALL_LINK
      }
      openInNewTab={content?.openInNewTab}
    />
  );
}
